// frontend/src/App.js
import React, { useEffect, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import api from "./apiClient";

import SignupForm from "./components/SignupForm";
import LoginForm from "./components/LoginForm";
import SplashPage from "./components/SplashPage";
import AdminDashboard from "./components/AdminDashboard";

import CarcPage from "./features/carc/CarcPage";
import EUAnalyzer from "./features/euanalyzer/EUAnalyzer"; // EUA
import TBSPage from "./features/tbs/TBSPage";
import BAPBApp from "./features/bapb/BAPBApp";
import NOVSPage from "./features/novs/NOVSPage";
import WindFarmsApp from "./features/wind/WindFarmsApp"; // NEW
import OGDB2App from "./features/ogdb2/OGDB2App";
import AWApp from "./features/aw/AWApp";
import ValueTrackerApp from "./features/valueTracker/ValueTrackerApp";

export default function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}

function AppRoutes() {
  const navigate = useNavigate();
  const location = useLocation();

  // ---------- App state ----------
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [loggedIn, setLoggedIn] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // prevent redirect-before-restore on refresh
  const [booting, setBooting] = useState(true);
  const [visibleApps, setVisibleApps] = useState(null);
  const [appsLoading, setAppsLoading] = useState(false);

  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  // Admin data
  const [adminTab, setAdminTab] = useState("pending");
  const [pendingUsers, setPendingUsers] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);

  // Tile handlers that need navigate
  const enterBAPB = () => navigate("/bapb");
  const enterNovs = () => navigate("/novs");
  const enterWind = () => navigate("/wind");
  const enterAW = () => navigate("/aw");
  const enterVT = () => navigate("/vt");

  // ---------- Boot: restore auth from localStorage ----------
  useEffect(() => {
    const token = localStorage.getItem("access_token");
    const storedEmail = localStorage.getItem("user_email");
    const storedIsAdmin = localStorage.getItem("is_admin"); // "1" | "0" | null
    if (token) {
      setLoggedIn(true);
      setEmail(storedEmail || "");
      setIsAdmin(storedIsAdmin === "1");
    }
    setBooting(false);
  }, []);

  // Load visible apps so we can guard routes per-app
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!loggedIn || !email) {
        setVisibleApps(null);
        return;
      }
      setAppsLoading(true);
      try {
        const res = await api.get("/apps/visible", { params: { email } });
        if (!cancelled) setVisibleApps(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled) setVisibleApps([]);
      } finally {
        if (!cancelled) setAppsLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [loggedIn, email, location.pathname]);

  // Ensure admin lists are loaded after refresh and when navigating to /admin
  useEffect(() => {
    if (!loggedIn || !isAdmin) return;
    fetchActiveUsers();
    fetchPendingUsers();
  }, [loggedIn, isAdmin]);

  // Also reload if you navigate into /admin later in the session
  useEffect(() => {
    if (!loggedIn || !isAdmin) return;
    if (location.pathname.startsWith("/admin")) {
      fetchActiveUsers();
      fetchPendingUsers();
    }
  }, [loggedIn, isAdmin, location.pathname]);

  // ---------- API helpers ----------
  const fetchPendingUsers = async () => {
    const res = await api.get("/pending_users");
    setPendingUsers(Array.isArray(res.data) ? res.data : []);
  };

  const fetchActiveUsers = async () => {
    const res = await api.get("/users");
    setActiveUsers(Array.isArray(res.data) ? res.data : []);
  };

  const approveUser = async (userEmail) => {
    await api.post(`/approve_user?email=${encodeURIComponent(userEmail)}`);
    await fetchPendingUsers();
    await fetchActiveUsers();
  };

  const rejectUser = async (userEmail) => {
    await api.post(`/reject_user?email=${encodeURIComponent(userEmail)}`);
    await fetchPendingUsers();
  };

  const deleteUser = async (userEmail) => {
    await api.post(`/delete_user?email=${encodeURIComponent(userEmail)}`);
    await fetchActiveUsers();
  };

  const toggleRole = async (userEmail, currentRole) => {
    await api.post(
      `/set_role?email=${encodeURIComponent(userEmail)}&is_admin=${!currentRole}`
    );
    await fetchActiveUsers();
  };


  // ---------- Auth actions ----------
  const login = async () => {
    try {
      const res = await api.post("/login", { email, password });
      if (res.data?.access_token) {
        localStorage.setItem("access_token", res.data.access_token);
      }
      localStorage.setItem("user_email", email);
      localStorage.setItem("is_admin", res.data?.is_admin ? "1" : "0");

      setLoggedIn(true);
      setIsAdmin(!!res.data?.is_admin);
      setError("");

      if (res.data?.is_admin) {
        await Promise.all([fetchPendingUsers(), fetchActiveUsers()]);
      }

      const from = location.state?.from?.pathname || "/apps";
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    }
  };

  const signup = async () => {
    try {
      const res = await api.post("/request_signup", { email, password });
      setMessage(res.data?.message || "Request submitted.");
      setError("");
    } catch (err) {
      setError(err.response?.data?.detail || "Signup failed");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("user_email");
    localStorage.removeItem("is_admin");

    setLoggedIn(false);
    setIsAdmin(false);
    setEmail("");
    setPassword("");
    setMessage("");
    setError("");

    setPendingUsers([]);
    setActiveUsers([]);
    setAdminTab("pending");

    navigate("/login", { replace: true });
  };

  // ---------- Route guards ----------
  const RequireAuth = ({ children }) => {
    if (booting) return <div className="p-6 text-gray-600">Loading…</div>;
    return loggedIn ? (
      children
    ) : (
      <Navigate to="/login" replace state={{ from: location }} />
    );
  };

  const RequireAdmin = ({ children }) =>
    isAdmin ? children : <Navigate to="/apps" replace />;

  // Guard that checks if a specific app is visible for this user
  const RequireApp = ({ appKey, children }) => {
    if (booting || appsLoading) {
      return <div className="p-6 text-gray-600">Loading…</div>;
    }
    if (!loggedIn) {
      return <Navigate to="/login" replace state={{ from: location }} />;
    }
    const allowed =
      Array.isArray(visibleApps) &&
      visibleApps.some((a) => {
        if (a && typeof a === "object" && a.key) {
          return String(a.key).toLowerCase() === String(appKey).toLowerCase();
        }
        return String(a).toLowerCase() === String(appKey).toLowerCase();
      });
    return allowed ? children : <Navigate to="/apps" replace />;
  };

  // ---------- Routes ----------
  return (
    <Routes>
      {/* Public: /login shows login or signup based on mode */}
      <Route
        path="/login"
        element={
          mode === "login" ? (
            <LoginForm
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              onSubmit={login}
              switchToSignup={() => {
                setMode("signup");
                setError("");
                setMessage("");
              }}
              error={error}
            />
          ) : (
            <SignupForm
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              onSubmit={signup}
              switchToLogin={() => {
                setMode("login");
                setError("");
                setMessage("");
              }}
              error={error}
              message={message}
            />
          )
        }
      />

      {/* Protected: splash */}
      <Route
        path="/apps"
        element={
          <RequireAuth>
            <SplashPage
			  email={email}
			  enterAdmin={() => navigate("/admin")}
			  enterCarc={() => navigate("/carc")}
			  enterEua={() => navigate("/eua")}
			  enterTbs={() => navigate("/tbs")}
			  enterBAPB={enterBAPB}
			  enterNovs={enterNovs}
			  enterWind={enterWind}
			  enterOgdb2={() => navigate("/ogdb2")}
			  enterAW={enterAW}
			  enterVT={enterVT}
			  logout={handleLogout}
			/>
          </RequireAuth>
        }
      />

      {/* === CARC — protected route === */}
      <Route
        path="/carc"
        element={
          <RequireAuth>
            <RequireApp appKey="carc">
              <CarcPage />
            </RequireApp>
          </RequireAuth>
        }
      />

      {/* === EUA — protected route === */}
      <Route
        path="/eua"
        element={
          <RequireAuth>
            <RequireApp appKey="eua">
              <EUAnalyzer
                email={email}
                onClose={() => navigate("/apps")}
                logout={handleLogout}
              />
            </RequireApp>
          </RequireAuth>
        }
      />
	  
	  {/* === OGDB2 — placeholder === */}
      <Route
        path="/ogdb2"
        element={
          <RequireAuth>
            <RequireApp appKey="ogdb2">
              <OGDB2App
                email={email}
                onClose={() => navigate("/apps")}
                logout={handleLogout}
              />
            </RequireApp>
          </RequireAuth>
        }
      />

      {/* === Wind Farms — protected route === */}
      <Route
        path="/wind"
        element={
          <RequireAuth>
            <RequireApp appKey="wind">
              <WindFarmsApp
                email={email}
                onClose={() => navigate("/apps")}
                logout={handleLogout}
              />
            </RequireApp>
          </RequireAuth>
        }
      />

      {/* === TBS — protected route === */}
      <Route
        path="/tbs"
        element={
          <RequireAuth>
            <RequireApp appKey="tbs">
              <TBSPage
                email={email}
                onClose={() => navigate("/apps")}
                logout={handleLogout}
              />
            </RequireApp>
          </RequireAuth>
        }
      />

      {/* === NOVS — protected route === */}
      <Route
        path="/novs"
        element={
          <RequireAuth>
            <RequireApp appKey="novs">
              <NOVSPage onClose={() => navigate("/apps")} logout={handleLogout} />
            </RequireApp>
          </RequireAuth>
        }
      />

      {/* === BAPB — protected route === */}
      <Route
        path="/bapb/*"
        element={
          <RequireAuth>
            <RequireApp appKey="bapb">
              <BAPBApp
                email={email}
                onClose={() => navigate("/apps")}
                logout={handleLogout}
              />
            </RequireApp>
          </RequireAuth>
        }
      />

      {/* === AW (Agent Watch) — protected route === */}
      <Route
        path="/aw/*"
        element={
          <RequireAuth>
            <RequireApp appKey="aw">
              <AWApp
                email={email}
                onClose={() => navigate("/apps")}
                logout={handleLogout}
              />
            </RequireApp>
          </RequireAuth>
        }
      />

      {/* === Value Tracker Config — protected route === */}
      <Route
        path="/vt/*"
        element={
          <RequireAuth>
            <RequireApp appKey="value_tracker_admin">
              <ValueTrackerApp
                onClose={() => navigate("/apps")}
              />
            </RequireApp>
          </RequireAuth>
        }
      />

      {/* Protected: admin */}
      <Route
        path="/admin"
        element={
          <RequireAuth>
            <RequireAdmin>
              <AdminDashboard
                email={email}
                logout={handleLogout}
                onClose={() => navigate("/apps")}
                pendingUsers={pendingUsers}
                activeUsers={activeUsers}
                fetchActiveUsers={fetchActiveUsers}
                approveUser={approveUser}
                rejectUser={rejectUser}
                deleteUser={deleteUser}
                toggleRole={toggleRole}
                adminTab={adminTab}
                setAdminTab={setAdminTab}
              />
            </RequireAdmin>
          </RequireAuth>
        }
      />

      {/* Default redirect */}
      <Route
        path="/"
        element={<Navigate to={loggedIn ? "/apps" : "/login"} replace />}
      />

      {/* 404 */}
      <Route
        path="*"
        element={
          <div className="p-8 text-center text-gray-500">404 – Page not found</div>
        }
      />
    </Routes>
  );
}
